/**
 * Los emoticonos que se ofrecen al escribir un correo o una nota.
 *
 * Lissette, 2026-09-03: *"agrega la opción de colocar emoticón en todos lados
 * donde se redacte correo y notas"*.
 *
 * ── POR QUE UNA LISTA CORTA Y ELEGIDA, Y NO UN TECLADO ENTERO ──────────────
 *
 * Un selector con los 3.700 emojis de Unicode se ve muy completo y no sirve
 * para nada: hay que buscar. Esto es una agencia de seguros escribiendo a sus
 * clientes, y lo que se pone en esos correos cabe en dos filas — un saludo,
 * una fecha, un aviso, unas gracias. Estan agrupados por para-que-sirven, con
 * el nombre en las dos lenguas para el buscador.
 *
 * ── LO QUE NO ESTA, A PROPOSITO ────────────────────────────────────────────
 *
 * Nada de caras que se puedan leer como burla, ni gestos con dedos, ni nada
 * con doble sentido. Un correo de una agencia a un cliente no es un chat entre
 * amigos, y el emoji equivocado en un aviso de cancelacion se lee fatal.
 *
 * ── SON TEXTO, NO IMAGENES ─────────────────────────────────────────────────
 *
 * Se insertan como el caracter tal cual. Asi viajan dentro del cuerpo del
 * correo sin adjuntos, sin CDN y sin tocar la CSP, y cada cliente los pinta
 * con el dibujo de SU telefono, que es como espera verlos.
 */

/** Grupos, en el orden en que se pintan. */
export const GRUPOS_DE_EMOTICONOS = [
  {
    id: 'saludo',
    es: 'Saludo y cortesía',
    en: 'Greetings',
    emojis: [
      { e: '👋', es: 'saludo hola mano', en: 'wave hello hand' },
      { e: '🙂', es: 'sonrisa cara', en: 'smile face' },
      { e: '😊', es: 'sonrisa contenta cara', en: 'smile happy face' },
      { e: '🙏', es: 'gracias por favor manos', en: 'thanks please hands' },
      { e: '🤝', es: 'acuerdo trato manos bienvenida', en: 'deal agreement handshake' },
      { e: '❤️', es: 'corazon cariño', en: 'heart love' },
      { e: '🎉', es: 'fiesta felicidades celebrar', en: 'party congrats celebrate' },
      { e: '🎂', es: 'cumpleaños pastel torta', en: 'birthday cake' },
      { e: '🎁', es: 'regalo', en: 'gift present' },
      { e: '⭐', es: 'estrella destacar', en: 'star' },
    ],
  },
  {
    id: 'aviso',
    es: 'Avisos y fechas',
    en: 'Notices & dates',
    emojis: [
      { e: '📅', es: 'fecha calendario cita', en: 'date calendar appointment' },
      { e: '⏰', es: 'hora reloj recordatorio', en: 'time clock reminder' },
      { e: '⚠️', es: 'aviso atencion cuidado', en: 'warning caution attention' },
      { e: '❗', es: 'importante urgente', en: 'important urgent' },
      { e: '✅', es: 'listo hecho correcto si', en: 'done check ok yes' },
      { e: '❌', es: 'no error cancelado', en: 'no error cancelled' },
      { e: '🔔', es: 'aviso campana notificacion', en: 'bell notice notification' },
      { e: '📌', es: 'importante fijar nota', en: 'pin important note' },
      { e: '👉', es: 'mira aqui señala', en: 'point look here' },
      { e: '🔁', es: 'renovacion repetir', en: 'renewal repeat' },
    ],
  },
  {
    id: 'seguros',
    es: 'Seguros y papeles',
    en: 'Insurance & paperwork',
    emojis: [
      { e: '📄', es: 'documento papel poliza', en: 'document paper policy' },
      { e: '📋', es: 'formulario lista aplicacion', en: 'form list application' },
      { e: '✍️', es: 'firma firmar', en: 'sign signature' },
      { e: '🏥', es: 'salud hospital medico', en: 'health hospital medical' },
      { e: '🦷', es: 'dental diente', en: 'dental tooth' },
      { e: '👓', es: 'vision lentes ojos', en: 'vision glasses eyes' },
      { e: '🚗', es: 'auto carro coche', en: 'auto car' },
      { e: '🏠', es: 'casa hogar propiedad', en: 'home house property' },
      { e: '🕊️', es: 'vida', en: 'life' },
      { e: '🐾', es: 'mascota perro gato', en: 'pet dog cat' },
    ],
  },
  {
    id: 'contacto',
    es: 'Dinero y contacto',
    en: 'Money & contact',
    emojis: [
      { e: '💵', es: 'dinero pago dolar', en: 'money payment dollar' },
      { e: '💳', es: 'tarjeta pago', en: 'card payment' },
      { e: '📞', es: 'telefono llamada llamar', en: 'phone call' },
      { e: '📱', es: 'celular movil mensaje', en: 'mobile cell text' },
      { e: '📧', es: 'correo email', en: 'email mail' },
      { e: '💬', es: 'mensaje chat comentario', en: 'message chat comment' },
      { e: '📍', es: 'direccion lugar mapa', en: 'address location map' },
      { e: '🔗', es: 'enlace link', en: 'link' },
      { e: '📎', es: 'adjunto clip archivo', en: 'attachment clip file' },
      { e: '🖨️', es: 'imprimir impresora', en: 'print printer' },
    ],
  },
];

/** Todos, en fila, por si alguien los quiere sin agrupar. */
export const EMOTICONOS = GRUPOS_DE_EMOTICONOS.flatMap((g) => g.emojis.map((x) => x.e));

function plano(s) {
  return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/**
 * Filtra por lo que se escriba, en español o en inglés y sin acentos.
 *
 * Con el buscador vacío devuelve los grupos tal cual. Un grupo que se queda
 * sin emojis NO se devuelve: un título con nada debajo solo ocupa sitio.
 */
export function buscarEmoticonos(texto) {
  const q = plano(texto).trim();
  if (!q) return GRUPOS_DE_EMOTICONOS;
  const trozos = q.split(/\s+/).filter(Boolean);
  const out = [];
  for (const g of GRUPOS_DE_EMOTICONOS) {
    const emojis = g.emojis.filter((x) => {
      const heno = plano(`${x.e} ${x.es} ${x.en}`);
      return trozos.every((t) => heno.includes(t));
    });
    if (emojis.length) out.push({ ...g, emojis });
  }
  return out;
}
